import { EMAIL_PROVIDER } from "@constants/ulm-constants";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";

/**
 * Type guard to check whether the passed value is of type EOAuthProvider
 * @param value
 * @returns whether the value is of type EOAuthProvider
 */
export function isOAuthProvider(
  value: string
): value is FaroCoreApiTypes.OAuthProvider {
  const providers: string[] = FaroCoreApiConst.allOAuthProviders;
  return providers.includes(value);
}

interface FilterOAuthProviders {
  /** Filtered list of available OAuth providers */
  providers: FaroCoreApiTypes.OAuthProvider[];
  /** Whether the email authentication method is allowed */
  isEmailAllowed: boolean;
}

/**
 * Filters a list of available providers by the passed providers query param.
 * The providers query param is a string representing a comma-separated list of providers.
 * @param availableProviders List of available providers
 * @param providersParam Providers query param
 * @returns An object with the filtered list of providers and whether the email option is allowed.
 * If the passed list has zero valid providers then all available providers will be returned.
 */
export function filterOAuthProviders(
  availableProviders: FaroCoreApiTypes.OAuthProvider[],
  providersParam: string | null
): FilterOAuthProviders {
  // If the param is not defined then return all available providers
  if (!providersParam) {
    return {
      providers: availableProviders,
      isEmailAllowed: true,
    };
  }

  const values = providersParam.split(",");

  // If there are not values passed then return all available providers
  if (!values.length) {
    return {
      providers: availableProviders,
      isEmailAllowed: true,
    };
  }

  const providers: FaroCoreApiTypes.OAuthProvider[] = [];
  const isEmailAllowed = values.includes(EMAIL_PROVIDER);

  // For each value check if it's a valid provider and if it's in the list of available providers
  values.forEach((value) => {
    if (
      isOAuthProvider(value) &&
      availableProviders.includes(value) &&
      !providers.includes(value)
    ) {
      providers.push(value);
    }
  });

  // If there were not valid providers or email provider passed then return all available providers
  if (!providers.length && !isEmailAllowed) {
    return {
      providers: availableProviders,
      isEmailAllowed: true,
    };
  }

  return {
    providers,
    isEmailAllowed,
  };
}
